/**
 * Centralized permission model for AlloyBuild.
 *
 * This is the single source of truth for role-based access control.
 * All permission checks (route guards, action gating, navigation filtering)
 * should go through `hasPermission()` or the `usePermission()` hook.
 *
 * Do not scatter ad-hoc role checks throughout the codebase.
 */

export type Permission =
  // Wallets
  | "wallet:create"
  | "wallet:read"
  | "wallet:full"
  // Transfers
  | "transfer:create"
  | "transfer:read"
  | "transfer:approve"
  | "transfer:reject"
  | "transfer:escalate"
  | "transfer:full"
  // Approvals
  | "approval:read"
  | "approval:approve"
  | "approval:reject"
  | "approval:hold"
  | "approval:escalate"
  // Admin
  | "admin:access"
  | "user:invite"
  | "user:suspend"
  | "user:read"
  | "user:full"
  | "role:read"
  | "role:full"
  | "group:read"
  | "group:full"
  | "service-account:manage"
  | "audit-log:read"
  // Configuration
  | "org:configure"
  | "auth:configure"
  | "settings:read"
  | "settings:full"
  // Oversight
  | "policy:read"
  | "policy:full"
  | "monitoring:read"
  | "reconciliation:run"
  | "evidence:read"
  | "evidence:export"
  // Treasury
  | "treasury:read"
  | "treasury:create-intent"
  | "treasury:execute";

/**
 * Canonical role keys. Both hyphen and underscore forms are accepted
 * so that URL params, fixtures, and tenant roleLabels can all resolve.
 */
export type AppRole =
  | "org-admin"
  | "security-admin"
  | "developer"
  | "wallet-admin"
  | "wallet_operator"
  | "compliance-officer"
  | "policy_approver"
  | "treasury-operator"
  | "finance_operator"
  | "finance_viewer"
  | "auditor"
  | "support-readonly"
  | "support_readonly";

const ROLE_PERMISSIONS: Record<AppRole, Permission[]> = {
  "org-admin": [
    "wallet:create",
    "wallet:read",
    "wallet:full",
    "transfer:create",
    "transfer:read",
    "transfer:approve",
    "transfer:reject",
    "transfer:escalate",
    "transfer:full",
    "approval:read",
    "approval:approve",
    "approval:reject",
    "approval:hold",
    "approval:escalate",
    "admin:access",
    "user:invite",
    "user:suspend",
    "user:read",
    "user:full",
    "role:read",
    "role:full",
    "group:read",
    "group:full",
    "service-account:manage",
    "audit-log:read",
    "org:configure",
    "auth:configure",
    "settings:read",
    "settings:full",
    "policy:read",
    "policy:full",
    "monitoring:read",
    "reconciliation:run",
    "evidence:read",
    "evidence:export",
    "treasury:read",
    "treasury:create-intent",
    "treasury:execute",
  ],
  "security-admin": [
    "wallet:read",
    "transfer:read",
    "transfer:approve",
    "transfer:reject",
    "transfer:escalate",
    "approval:read",
    "approval:approve",
    "approval:reject",
    "approval:hold",
    "approval:escalate",
    "admin:access",
    "user:invite",
    "user:suspend",
    "user:read",
    "user:full",
    "role:read",
    "role:full",
    "group:read",
    "service-account:manage",
    "audit-log:read",
    "auth:configure",
    "settings:read",
    "policy:read",
    "policy:full",
    "monitoring:read",
    "evidence:read",
    "evidence:export",
  ],
  "developer": [
    "wallet:read",
    "transfer:read",
    "approval:read",
    "service-account:manage",
    "audit-log:read",
    "settings:read",
    "monitoring:read",
    "evidence:read",
    "treasury:read",
  ],
  "wallet-admin": [
    "wallet:create",
    "wallet:read",
    "wallet:full",
    "transfer:read",
    "approval:read",
    "user:read",
    "settings:read",
    "policy:read",
    "monitoring:read",
    "evidence:read",
    "treasury:read",
  ],
  wallet_operator: [
    "wallet:create",
    "wallet:read",
    "transfer:create",
    "transfer:read",
    "approval:read",
    "settings:read",
    "policy:read",
    "monitoring:read",
    "evidence:read",
    "treasury:read",
    "treasury:create-intent",
  ],
  "compliance-officer": [
    "wallet:read",
    "transfer:read",
    "transfer:approve",
    "transfer:reject",
    "transfer:escalate",
    "approval:read",
    "approval:approve",
    "approval:reject",
    "approval:hold",
    "approval:escalate",
    "user:read",
    "audit-log:read",
    "settings:read",
    "policy:read",
    "policy:full",
    "monitoring:read",
    "reconciliation:run",
    "evidence:read",
    "evidence:export",
    "treasury:read",
  ],
  policy_approver: [
    "wallet:read",
    "transfer:read",
    "transfer:approve",
    "transfer:reject",
    "transfer:escalate",
    "approval:read",
    "approval:approve",
    "approval:reject",
    "approval:hold",
    "approval:escalate",
    "policy:read",
    "monitoring:read",
    "evidence:read",
    "treasury:read",
  ],
  "treasury-operator": [
    "wallet:read",
    "transfer:create",
    "transfer:read",
    "approval:read",
    "settings:read",
    "policy:read",
    "monitoring:read",
    "evidence:read",
    "treasury:read",
    "treasury:create-intent",
  ],
  finance_operator: [
    "wallet:read",
    "transfer:read",
    "approval:read",
    "approval:approve",
    "approval:reject",
    "approval:hold",
    "approval:escalate",
    "reconciliation:run",
    "evidence:read",
    "evidence:export",
    "treasury:read",
    "treasury:create-intent",
    "treasury:execute",
  ],
  finance_viewer: [
    "wallet:read",
    "transfer:read",
    "approval:read",
    "monitoring:read",
    "evidence:read",
    "treasury:read",
  ],
  auditor: [
    "wallet:read",
    "transfer:read",
    "approval:read",
    "user:read",
    "role:read",
    "group:read",
    "audit-log:read",
    "settings:read",
    "policy:read",
    "monitoring:read",
    "evidence:read",
    "evidence:export",
    "treasury:read",
  ],
  "support-readonly": [
    "user:read",
    "audit-log:read",
    "settings:read",
  ],
  support_readonly: [
    "user:read",
    "audit-log:read",
    "settings:read",
  ],
};

function normalizeRole(role: string | null | undefined): AppRole | null {
  if (!role) return null;
  const key = role.trim().toLowerCase().replace(/\s+/g, "-");
  // Accept both "org admin" and "org-admin" style labels.
  const aliasMap: Record<string, AppRole> = {
    "org-admin": "org-admin",
    "org_admin": "org-admin",
    "organization-admin": "org-admin",
    "security-admin": "security-admin",
    security_admin: "security-admin",
    developer: "developer",
    "wallet-admin": "wallet-admin",
    wallet_admin: "wallet-admin",
    "wallet-operator": "wallet_operator",
    wallet_operator: "wallet_operator",
    "compliance-officer": "compliance-officer",
    compliance_officer: "compliance-officer",
    "policy-approver": "policy_approver",
    policy_approver: "policy_approver",
    "treasury-operator": "treasury-operator",
    treasury_operator: "treasury-operator",
    "finance-operator": "finance_operator",
    finance_operator: "finance_operator",
    "finance-viewer": "finance_viewer",
    finance_viewer: "finance_viewer",
    auditor: "auditor",
    "support-readonly": "support-readonly",
    support_readonly: "support-readonly",
    "support-read-only": "support-readonly",
    "sandbox-designer": "org-admin",
    sandbox_designer: "org-admin",
    "sandbox-user": "wallet_operator",
    sandbox_user: "wallet_operator",
  };
  return aliasMap[key] ?? null;
}

const BACKEND_PERMISSION_MAP: Record<Permission, string[]> = {
  "wallet:create": ["walletkit.wallet.create"],
  "wallet:read": ["walletkit.wallet.read"],
  "wallet:full": ["walletkit.wallet.create", "walletkit.wallet.read"],
  "transfer:create": ["walletkit.transfer.create"],
  "transfer:read": ["walletkit.transfer.read"],
  "transfer:approve": ["walletkit.transfer.approve"],
  "transfer:reject": ["walletkit.transfer.reject"],
  "transfer:escalate": ["walletkit.transfer.escalate"],
  "transfer:full": ["walletkit.transfer.create", "walletkit.transfer.read"],
  "approval:read": ["walletkit.approval.read"],
  "approval:approve": ["walletkit.approval.approve"],
  "approval:reject": ["walletkit.approval.reject"],
  "approval:hold": ["walletkit.approval.hold"],
  "approval:escalate": ["walletkit.approval.escalate"],
  "admin:access": ["admin.portal.access"],
  "user:invite": ["admin.user.invite"],
  "user:suspend": ["admin.user.suspend"],
  "user:read": ["admin.user.read"],
  "user:full": ["admin.user.manage"],
  "role:read": ["admin.role.read"],
  "role:full": ["admin.role.manage"],
  "group:read": ["admin.group.read"],
  "group:full": ["admin.group.manage"],
  "service-account:manage": ["admin.service-account.manage"],
  "audit-log:read": ["admin.audit.read"],
  "org:configure": ["admin.org.configure"],
  "auth:configure": ["admin.auth.configure"],
  "settings:read": ["admin.settings.read"],
  "settings:full": ["admin.settings.manage"],
  "policy:read": ["policykit.policy.read"],
  "policy:full": ["policykit.policy.manage"],
  "monitoring:read": ["monitoring.read"],
  "reconciliation:run": ["reconciliation.run"],
  "evidence:read": ["evidence.read"],
  "evidence:export": ["evidence.export"],
  "treasury:read": ["treasury.read"],
  "treasury:create-intent": ["treasury.intent.create"],
  "treasury:execute": ["treasury.execute"],
};

export function hasBackendPermission(grantedPermissions: string[], permission: Permission): boolean {
  const backendKeys = BACKEND_PERMISSION_MAP[permission];
  if (!backendKeys) return false;
  return backendKeys.some((key) => grantedPermissions.includes(key));
}

export function hasPermission(role: string | null | undefined, permission: Permission): boolean {
  const normalized = normalizeRole(role);
  if (!normalized) return false;
  const perms = ROLE_PERMISSIONS[normalized];
  if (!perms) return false;
  return perms.includes(permission);
}

export function hasAnyPermission(role: string | null | undefined, permissions: Permission[]): boolean {
  return permissions.some((p) => hasPermission(role, p));
}

export function hasAllPermissions(role: string | null | undefined, permissions: Permission[]): boolean {
  return permissions.every((p) => hasPermission(role, p));
}

/**
 * Convenience check for whether a role may enter the admin portal.
 */
export function canAccessAdmin(role: string | null | undefined): boolean {
  return hasPermission(role, "admin:access");
}

/**
 * Convenience check for whether a role may access organization settings.
 */
export function canAccessSettings(role: string | null | undefined): boolean {
  return hasPermission(role, "settings:read");
}

/**
 * Map of role display labels for the current auth context.
 */
export function roleDisplayLabel(role: string | null | undefined): string {
  if (!role) return "Unknown";
  const normalized = normalizeRole(role);
  const labels: Record<AppRole, string> = {
    "org-admin": "Organization Admin",
    "security-admin": "Security Admin",
    developer: "Developer",
    "wallet-admin": "Wallet Administrator",
    wallet_operator: "Wallet Operator",
    "compliance-officer": "Compliance Officer",
    policy_approver: "Policy Approver",
    "treasury-operator": "Treasury Operator",
    finance_operator: "Finance Operator",
    finance_viewer: "Finance Viewer",
    auditor: "Auditor",
    "support-readonly": "Support Read-Only",
    support_readonly: "Support Read-Only",
  };
  return normalized ? labels[normalized] : role;
}
